/**
 * How much article is actually there — the number a page list shows next to a title.
 *
 * Pure and dependency-free so it can be unit-tested: CRA's jest cannot resolve
 * react-router-dom v7, and every list that wants this count is a component that
 * imports the router.
 *
 * Counting words rather than bytes because the question being asked of a page list
 * is "is this one written yet, and roughly how long", and bytes answer neither —
 * markdown syntax and a UTF-8 em dash both inflate them.
 */

/** Markdown/HTML that is punctuation to a reader and must not count as a word. */
const TAG_RE = /<[^>]*>/g;
const CODE_FENCE_RE = /^[ \t]*`{3,}.*$/gm;

/**
 * Words in a markdown body. Zero for null, empty, or markup-only content.
 *
 * A "word" has to carry at least one letter or digit, so heading hashes, list
 * bullets, table pipes and `---` rules are ignored — otherwise an outline-only
 * page reads as if it had prose in it.
 */
export function countWords(content: string | null | undefined): number {
  if (!content) return 0;
  const prose = content.replace(CODE_FENCE_RE, ' ').replace(TAG_RE, ' ');
  const tokens = prose.split(/\s+/);
  let count = 0;
  for (const token of tokens) {
    if (/[\p{L}\p{N}]/u.test(token)) count += 1;
  }
  return count;
}

/**
 * The count as it appears in a narrow sidebar: "820 words", "1.2k words", and
 * "no content" for nothing.
 *
 * Zero says "no content" and not a dash. A whole topic can sit at zero — a page
 * carrying a stale READY_TO_DEPLOY badge over an empty version is not unusual —
 * and a column of identical dashes reads as decoration rather than as an answer.
 *
 * Thousands are abbreviated because the exact figure is not what the glance is
 * for; the title next to it needs the room more, and the tooltip has the number.
 */
export function formatWordCount(words: number): string {
  if (words <= 0) return 'no content';
  if (words < 1000) return `${words} words`;
  return `${(words / 1000).toFixed(1).replace(/\.0$/, '')}k words`;
}
